import React, { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { collection, getDocs, query, orderBy, doc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { Mail, Send, Users, Trash2, Loader2, AlertTriangle, Check, X, Eye, FlaskConical, RefreshCw, Save, Clock, CreditCard, History, ChevronDown, FileText, Film } from 'lucide-react';
import RenewalSequenceEditor from './RenewalSequenceEditor';
import EmailTransaksiEditor, { EmailTransaksiSenarai } from './EmailTransaksiEditor';
import { useTemplatTransaksi, NAMA_PENDEK } from '../hooks/useTemplatTransaksi';
import { useTemplatHosted, TEMPLAT_HOSTED } from '../hooks/useTemplatHosted';
import '../design/tokens.css';
import '../design/ui.css';
import './EmailBlastTab.css';

// Satu baris menu kiri — rupa SAMA seperti rel tab Flow Ultra (tajuk + baris kecil).
function RailRow({ on, icon, title, sub, onClick }) {
    const Ikon = icon; // ESLint webapp tiada jsx-uses-vars — ikon dari parameter mesti lalu const
    return (
        <button type="button" className={`s-navrow${on ? ' s-navrow--on' : ''}`}
            style={{ height: 'auto', padding: '7px 8px' }} onClick={onClick}>
            <span className="s-navrow__icon"><Ikon size={16} strokeWidth={1.9} /></span>
            <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <span style={{ display: 'block' }}>{title}</span>
                <span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>{sub}</span>
            </span>
        </button>
    );
}

function SecHead({ title, sub, right }) {
    return (
        <div className="em-sec">
            <div><h2>{title}</h2><p>{sub}</p></div>
            {right}
        </div>
    );
}

const previewFn = httpsCallable(functions, 'adminEmailAudiencePreview');
const saveDraftFn = httpsCallable(functions, 'adminEmailSaveDraft');
const sendTestFn = httpsCallable(functions, 'adminEmailSendTest');
const launchFn = httpsCallable(functions, 'adminEmailLaunch');

// Client-side preview render — mirrors the server markdownToHtml lightly.
function renderBodyPreview(text) {
    let s = String(text || '');
    s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" style="color:#a78bfa">$1</a>');
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    return s.split(/\n\s*\n/).map(p => `<p style="margin:0 0 16px;line-height:1.6">${p.replace(/\n/g, '<br>')}</p>`).join('');
}

const SEG_LABELS = { all: 'All users', tier: 'By tier', expiring_Nd: 'License expiring', campaign: 'By campaign tag', license_expired: 'License expired' };
const STATUS_BADGE = {
    draft: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    sending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    sent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    failed: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
};

function segmentLabel(seg) {
    if (!seg) return '—';
    const base = SEG_LABELS[seg.type] || seg.type;
    if (seg.type === 'tier') return `${base}: ${seg.params?.tier || '?'}`;
    if (seg.type === 'expiring_Nd') return `${base}: ${seg.params?.days || 7} days`;
    if (seg.type === 'campaign') return `${base}: ${seg.params?.tag || '?'}`;
    return base;
}

export default function EmailBlastTab() {
    const [view, setView] = useState('compose'); // 'compose' | 'history' | 'renewal' | 'payment'
    // Folder "Payment emails" dalam menu kiri: buka/tutup + templat yang sedang diedit (null = ringkasan).
    const [bukaPayment, setBukaPayment] = useState(false);
    const [templatId, setTemplatId] = useState(null);
    const txn = useTemplatTransaksi();
    const hosted = useTemplatHosted(); // ★ Hosted Flow Notifications — menu berasingan, doc sama
    const txnDirtyRef = useRef(false);
    const setTxnDirty = useCallback((d) => { txnDirtyRef.current = d; }, []);

    // Satu pintu navigasi — elak suntingan templat yang belum disimpan hilang senyap.
    const pergi = (nextView, nextTemplat = null) => {
        const beralih = nextView !== view || nextTemplat !== templatId;
        if (beralih && txnDirtyRef.current && !window.confirm('You have unsaved changes on this email template. Leave without saving?')) return false;
        if (beralih) txnDirtyRef.current = false;
        setView(nextView);
        setTemplatId(nextTemplat);
        return true;
    };

    const [campaigns, setCampaigns] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [templates, setTemplates] = useState([]);

    const [subject, setSubject] = useState('');
    const [bodyText, setBodyText] = useState('');
    const [segType, setSegType] = useState('all');
    const [segTier, setSegTier] = useState('paid');
    const [segDays, setSegDays] = useState(7);
    const [segTag, setSegTag] = useState('');

    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState('');
    const [msg, setMsg] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [draftId, setDraftId] = useState(null);

    const buildSegment = useCallback(() => {
        if (segType === 'tier') return { type: 'tier', params: { tier: segTier } };
        if (segType === 'expiring_Nd') return { type: 'expiring_Nd', params: { days: Number(segDays) || 7 } };
        if (segType === 'campaign') return { type: 'campaign', params: { tag: segTag.trim() } };
        if (segType === 'license_expired') return { type: 'license_expired', params: {} };
        return { type: 'all', params: {} };
    }, [segType, segTier, segDays, segTag]);

    const loadCampaigns = useCallback(async () => {
        setLoadingList(true);
        try {
            const snap = await getDocs(query(collection(db, 'emailCampaigns'), orderBy('createdAt', 'desc')));
            setCampaigns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error('[EmailBlast] load campaigns failed:', e);
        } finally {
            setLoadingList(false);
        }
    }, []);

    const loadTemplates = useCallback(async () => {
        try {
            const snap = await getDocs(query(collection(db, 'email_templates'), orderBy('createdAt', 'desc')));
            setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error('[EmailBlast] load templates failed:', e);
        }
    }, []);

    useEffect(() => { loadCampaigns(); loadTemplates(); }, [loadCampaigns, loadTemplates]);
    useEffect(() => { setPreview(null); }, [segType, segTier, segDays, segTag]);

    const applyTemplate = (id) => {
        const t = templates.find(x => x.id === id);
        if (t) { setSubject(t.subject || ''); setBodyText(t.bodyText || ''); setMsg({ type: 'success', text: `Template "${t.name}" loaded.` }); }
    };

    const saveTemplate = async () => {
        if (!subject || !bodyText) { setMsg({ type: 'error', text: 'Fill in the subject & email body first.' }); return; }
        const name = window.prompt('Template name:', subject);
        if (!name) return;
        try {
            await addDoc(collection(db, 'email_templates'), { name, subject, bodyText, createdAt: serverTimestamp() });
            setMsg({ type: 'success', text: `Template "${name}" saved.` });
            loadTemplates();
        } catch (e) {
            setMsg({ type: 'error', text: 'Failed to save template: ' + e.message });
        }
    };

    const handlePreview = async () => {
        setBusy('preview'); setMsg(null);
        try {
            const r = await previewFn({ segment: buildSegment() });
            setPreview(r.data);
        } catch (e) {
            setMsg({ type: 'error', text: e.message || 'Failed to check count.' });
        } finally { setBusy(''); }
    };

    const handleTest = async () => {
        if (!subject || !bodyText) { setMsg({ type: 'error', text: 'Fill in the subject & email body first.' }); return; }
        setBusy('test'); setMsg(null);
        try {
            const r = await sendTestFn({ subject, bodyText });
            setMsg({
                type: 'success',
                text: r.data.dryRun
                    ? `Practice mode: test email "${subject}" prepared (NO real email sent). Set the Resend API key to send for real.`
                    : `Test email sent to ${r.data.to}. Please check your inbox.`,
            });
        } catch (e) {
            setMsg({ type: 'error', text: e.message || 'Failed to send test.' });
        } finally { setBusy(''); }
    };

    const handleSaveDraft = async () => {
        if (!subject || !bodyText) { setMsg({ type: 'error', text: 'Fill in the subject & email body first.' }); return; }
        setBusy('save'); setMsg(null);
        try {
            const r = await saveDraftFn({ id: draftId, subject, bodyText, segment: buildSegment() });
            setDraftId(r.data.id);
            setMsg({ type: 'success', text: 'Draft saved.' });
            loadCampaigns();
        } catch (e) {
            setMsg({ type: 'error', text: e.message || 'Failed to save draft.' });
        } finally { setBusy(''); }
    };

    const handleLaunch = async () => {
        setConfirmOpen(false);
        setBusy('launch'); setMsg(null);
        try {
            const saved = await saveDraftFn({ id: draftId, subject, bodyText, segment: buildSegment() });
            const id = saved.data.id;
            const r = await launchFn({ campaignId: id });
            const d = r.data;
            setMsg({
                type: 'success',
                text: d.dryRun
                    ? `Practice mode: ${d.sentCount} emails prepared (NO real emails sent). ${d.skippedSuppressed} skipped (unsubscribed).`
                    : `Blast complete! ${d.sentCount} sent, ${d.failedCount} failed, ${d.skippedSuppressed} skipped (unsubscribed).`,
            });
            setSubject(''); setBodyText(''); setDraftId(null); setPreview(null);
            loadCampaigns();
        } catch (e) {
            setMsg({ type: 'error', text: e.message || 'Failed to send blast.' });
        } finally { setBusy(''); }
    };

    const handleDeleteDraft = async (id) => {
        if (!window.confirm('Delete this draft?')) return;
        try {
            await deleteDoc(doc(db, 'emailCampaigns', id));
            if (draftId === id) setDraftId(null);
            loadCampaigns();
        } catch (e) {
            setMsg({ type: 'error', text: 'Failed to delete: ' + e.message });
        }
    };

    const canLaunch = subject && bodyText && preview && preview.count > 0 && !busy;

    return (
        <div className="s-root em-shell">
            <div style={{ marginBottom: 24 }}>
                <h1 className="s-title">Email</h1>
                <p className="s-subtitle">Send blasts, renewal reminders and payment emails to customers.</p>
            </div>

            <div className="em-grid">
                <nav className="em-rail">
                    <RailRow on={view === 'compose'} icon={Mail} title="Write & send" sub="One-off blast" onClick={() => pergi('compose')} />
                    <RailRow on={view === 'history'} icon={History} title="History"
                        sub={loadingList ? 'Loading…' : `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`}
                        onClick={() => pergi('history')} />
                    <div style={{ height: 1, background: 'var(--s-line)', margin: '9px 4px' }} />
                    <RailRow on={view === 'renewal'} icon={Clock} title="Renewal reminders" sub="Before & after expiry" onClick={() => pergi('renewal')} />
                    {/* Folder: tekan = buka + ringkasan; anak = borang edit satu templat */}
                    <button type="button" className={`s-navrow${view === 'payment' && !templatId ? ' s-navrow--on' : ''}`}
                        style={{ height: 'auto', padding: '7px 8px' }}
                        aria-expanded={bukaPayment}
                        onClick={() => { if (pergi('payment', null)) setBukaPayment(true); }}>
                        <span className="s-navrow__icon"><CreditCard size={16} strokeWidth={1.9} /></span>
                        <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                            <span style={{ display: 'block' }}>Payment emails</span>
                            <span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>
                                {txn.loading ? 'Loading…' : `${txn.items.filter(t => t.enabled).length} of ${txn.items.length} ON`}
                            </span>
                        </span>
                        <span className="em-count">{txn.items.length}</span>
                        <span className={`em-chev${bukaPayment ? ' em-chev--open' : ''}`} role="button" tabIndex={0}
                            aria-label={bukaPayment ? 'Collapse' : 'Expand'}
                            onClick={(e) => { e.stopPropagation(); setBukaPayment(o => !o); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setBukaPayment(o => !o); } }}>
                            <ChevronDown size={14} strokeWidth={2} />
                        </span>
                    </button>
                    {bukaPayment && (
                        <div className="em-folder">
                            {txn.items.map(t => (
                                <button key={t.id} type="button" title={t.nama}
                                    className={`em-leaf${view === 'payment' && templatId === t.id ? ' em-leaf--on' : ''}`}
                                    onClick={() => pergi('payment', t.id)}>
                                    <FileText size={14} strokeWidth={1.9} className="em-leaf__ic" />
                                    <span className="em-leaf__n">{NAMA_PENDEK[t.id] || t.nama}</span>
                                    <span className={`em-dot${t.enabled ? ' em-dot--on' : ''}`} title={t.enabled ? 'ON' : 'OFF'} />
                                </button>
                            ))}
                        </div>
                    )}
                    {/* ★ Hosted Flow Notifications — menu berasingan, SEBARIS Payment emails (satu templat automatik) */}
                    <RailRow on={view === 'hosted'} icon={Film} title="Hosted Flow Notifications"
                        sub={hosted.loading ? 'Loading…' : (hosted.items[0]?.enabled ? 'ON' : 'OFF — default')}
                        onClick={() => pergi('hosted')} />
                </nav>

                <main style={{ minWidth: 0 }}>
            {view === 'payment' ? (
                <>
                    {(() => {
                        const pilih = templatId ? txn.items.find(t => t.id === templatId) : null;
                        if (!pilih) return (
                            <>
                                <SecHead title="Payment emails" sub="Sent the moment a payment is confirmed. Pick a template to edit it." />
                                <EmailTransaksiSenarai items={txn.items} loading={txn.loading} ralat={txn.ralat}
                                    onPilih={(id) => { if (pergi('payment', id)) setBukaPayment(true); }} />
                            </>
                        );
                        return (
                            <>
                                <SecHead title={pilih.nama} sub={pilih.bila} />
                                {txn.loading
                                    ? <div className="py-12 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>
                                    : <EmailTransaksiEditor key={pilih.id} templat={pilih} simpanSatu={txn.simpanSatu} onDirtyChange={setTxnDirty} />}
                            </>
                        );
                    })()}
                </>
            ) : view === 'hosted' ? (
                <>
                    <SecHead title="Hosted Flow Notifications" sub="Automatic summary email when a customer's Hosted Flow batch finishes (one email per batch). Transactional, not marketing." />
                    <div className="flex items-start gap-2 p-3 rounded-xl mb-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-sm">
                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                        <span>Default <strong>OFF</strong>. Proofread, press <em>Send test to me</em>, then switch ON. Per-batch delivery status &amp; retry live under <strong>Flow Ultra → Hosted Jobs</strong>.</span>
                    </div>
                    {hosted.loading
                        ? <div className="py-12 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>
                        : <EmailTransaksiEditor key={hosted.items[0].id} templat={hosted.items[0]} simpanSatu={hosted.simpanSatu} onDirtyChange={setTxnDirty} templatLalai={TEMPLAT_HOSTED} />}
                </>
            ) : view === 'renewal' ? (
                <>
                    <SecHead title="Renewal reminders" sub="Sent by the system every day at 9 AM, before or after a license expires." />
                    <RenewalSequenceEditor />
                </>
            ) : view === 'history' ? (
                <>
                    <SecHead title="History" sub="Every blast sent or saved as a draft, with opens and clicks."
                        right={(
                            <button onClick={loadCampaigns} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 shrink-0">
                                <RefreshCw size={14} className={loadingList ? 'animate-spin' : ''} /> Refresh
                            </button>
                        )} />
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                        {loadingList ? (
                            <div className="py-8 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>
                        ) : campaigns.length === 0 ? (
                            <p className="py-8 text-center text-slate-400 text-sm">No campaigns yet. Write your first email under Write &amp; send.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-100 dark:border-slate-800">
                                            <th className="py-2 pr-4">Subject</th>
                                            <th className="py-2 pr-4">Recipients</th>
                                            <th className="py-2 pr-4">Status</th>
                                            <th className="py-2 pr-4">Sent</th>
                                            <th className="py-2 pr-4">Open / Click</th>
                                            <th className="py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {campaigns.map(c => (
                                            <tr key={c.id} className="border-b border-slate-50 dark:border-slate-800/50">
                                                <td className="py-3 pr-4">
                                                    <div className="font-medium text-slate-700 dark:text-slate-200">{c.subject || '(no subject)'}</div>
                                                    <div className="text-xs text-slate-400">{segmentLabel(c.segment)}</div>
                                                </td>
                                                <td className="py-3 pr-4 text-slate-500">{c.recipientCountEstimate ?? '—'}</td>
                                                <td className="py-3 pr-4">
                                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[c.status] || STATUS_BADGE.draft}`}>
                                                        {c.status}{c.dryRun ? ' (practice)' : ''}
                                                    </span>
                                                </td>
                                                <td className="py-3 pr-4 text-slate-500">
                                                    {c.status === 'sent' ? `${c.sentCount || 0} ✓ / ${c.failedCount || 0} ✗` : '—'}
                                                </td>
                                                <td className="py-3 pr-4 text-slate-500">
                                                    {c.status === 'sent' ? `${c.openCount || 0} / ${c.clickCount || 0}` : '—'}
                                                </td>
                                                <td className="py-3 text-right">
                                                    {c.status === 'draft' && (
                                                        <button onClick={() => handleDeleteDraft(c.id)} className="text-slate-400 hover:text-rose-500" title="Delete draft">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <SecHead title="Write & send" sub="Compose one email, pick who receives it, then send." />
                    {/* Practice-mode hint */}
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-sm">
                        <FlaskConical size={16} className="mt-0.5 shrink-0" />
                        <span>While the Resend API key isn't set, the system is in <strong>practice mode</strong> — every step runs but <strong>no real emails are sent</strong>. Safe to test.</span>
                    </div>

                    {msg && (
                        <div className={`flex items-start gap-2 p-3 rounded-xl text-sm ${msg.type === 'error' ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'}`}>
                            {msg.type === 'error' ? <AlertTriangle size={16} className="mt-0.5 shrink-0" /> : <Check size={16} className="mt-0.5 shrink-0" />}
                            <span>{msg.text}</span>
                            <button onClick={() => setMsg(null)} className="ml-auto opacity-60 hover:opacity-100"><X size={16} /></button>
                        </div>
                    )}

                    <div className="grid lg:grid-cols-2 gap-6">
                        {/* ─── Composer ─── */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm space-y-5">
                            {/* Templates */}
                            <div className="flex flex-wrap items-center gap-2">
                                <select value="" onChange={e => e.target.value && applyTemplate(e.target.value)}
                                    className="flex-1 min-w-[160px] p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 outline-none">
                                    <option value="">— Load template —</option>
                                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                                <button onClick={saveTemplate} className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700">
                                    <Save size={15} /> Save template
                                </button>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Subject</label>
                                <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. SESAAT year-end promo 🎉"
                                    className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-violet-500 outline-none" />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email Body</label>
                                <textarea value={bodyText} onChange={e => setBodyText(e.target.value)} rows={8} placeholder={'Write normally.\n\nUse **bold** for emphasis, and [link text](https://app.sesaat.ai) for links.'}
                                    className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-violet-500 outline-none resize-none font-mono" />
                                <p className="text-[11px] text-slate-400 mt-1">Tip: <code>**bold**</code> · <code>[text](link)</code> · blank line = new paragraph.</p>
                            </div>

                            {/* Segment picker */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Send To</label>
                                <select value={segType} onChange={e => setSegType(e.target.value)}
                                    className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-violet-500 outline-none">
                                    <option value="all">All users</option>
                                    <option value="tier">By tier (free / trial / paid)</option>
                                    <option value="expiring_Nd">License expiring in X days</option>
                                    <option value="license_expired">License expired</option>
                                    <option value="campaign">By campaign tag</option>
                                </select>
                                {segType === 'tier' && (
                                    <select value={segTier} onChange={e => setSegTier(e.target.value)}
                                        className="mt-2 w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 outline-none">
                                        <option value="free">Free</option>
                                        <option value="trial">Trial</option>
                                        <option value="paid">Paid</option>
                                    </select>
                                )}
                                {segType === 'expiring_Nd' && (
                                    <div className="mt-2 flex items-center gap-2">
                                        <span className="text-sm text-slate-500">Expiring within</span>
                                        <input type="number" min={1} max={90} value={segDays} onChange={e => setSegDays(e.target.value)}
                                            className="w-20 p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-slate-50 dark:bg-slate-800 outline-none" />
                                        <span className="text-sm text-slate-500">days</span>
                                    </div>
                                )}
                                {segType === 'campaign' && (
                                    <input type="text" value={segTag} onChange={e => setSegTag(e.target.value)} placeholder="campaign tag name"
                                        className="mt-2 w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 outline-none" />
                                )}
                            </div>

                            {/* Preview count */}
                            <div className="flex items-center gap-3">
                                <button onClick={handlePreview} disabled={!!busy}
                                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition disabled:opacity-50">
                                    {busy === 'preview' ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
                                    Check Count
                                </button>
                                {preview && (
                                    <span className="text-sm text-slate-600 dark:text-slate-300">
                                        <strong className="text-violet-600 dark:text-violet-400">{preview.count}</strong> people will receive
                                        {preview.suppressed > 0 && <span className="text-slate-400"> ({preview.suppressed} unsubscribed skipped)</span>}
                                    </span>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                                <button onClick={handleTest} disabled={!!busy}
                                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition disabled:opacity-50">
                                    {busy === 'test' ? <Loader2 size={16} className="animate-spin" /> : <FlaskConical size={16} />}
                                    Send Test to Me
                                </button>
                                <button onClick={handleSaveDraft} disabled={!!busy}
                                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition disabled:opacity-50">
                                    Save Draft
                                </button>
                                <button onClick={() => setConfirmOpen(true)} disabled={!canLaunch}
                                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold hover:brightness-110 transition disabled:opacity-40 ml-auto">
                                    {busy === 'launch' ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                    Launch Blast
                                </button>
                            </div>
                            {!preview && subject && bodyText && (
                                <p className="text-[11px] text-amber-500">Click "Check Count" first before you can launch.</p>
                            )}
                        </div>

                        {/* ─── Live preview ─── */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                            <div className="flex items-center gap-2 mb-4 text-slate-500"><Eye size={16} /><span className="text-xs font-bold uppercase">Email Preview</span></div>
                            <div className="rounded-xl overflow-hidden" style={{ background: '#0f172a', padding: '24px' }}>
                                <div style={{ textAlign: 'center', marginBottom: 12 }}><span style={{ color: '#a78bfa', fontWeight: 'bold' }}>⚡ SESAAT.AI</span></div>
                                <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, color: '#f1f5f9', fontSize: 14 }}>
                                    {subject && <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 12 }}>{subject}</div>}
                                    <div dangerouslySetInnerHTML={{ __html: renderBodyPreview(bodyText) || '<p style="color:#64748b">Your email body will appear here…</p>' }} />
                                </div>
                                <div style={{ textAlign: 'center', marginTop: 12 }}>
                                    <p style={{ color: '#475569', fontSize: 11, margin: 0 }}>SESAAT.AI · Malaysia</p>
                                    <p style={{ color: '#475569', fontSize: 11, margin: '4px 0 0' }}>Unsubscribe (auto-added)</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ─── Confirm launch modal ─── */}
                    {confirmOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmOpen(false)}>
                            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center"><AlertTriangle size={20} className="text-amber-600 dark:text-amber-400" /></div>
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">Confirm Blast</h3>
                                </div>
                                <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                                    The email <strong>"{subject}"</strong> will be sent to <strong className="text-violet-600 dark:text-violet-400">{preview?.count} people</strong>.
                                    This action <strong>cannot be undone</strong>.
                                </p>
                                <div className="flex gap-2">
                                    <button onClick={() => setConfirmOpen(false)} className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700">
                                        Cancel
                                    </button>
                                    <button onClick={handleLaunch} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold hover:brightness-110">
                                        Yes, Send Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
                </main>
            </div>
        </div>
    );
}
